import { clubPath, clubUrl } from '../../clubhouse';
import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, QrCode, TrendingUp, Gift, Loader2, X, Send, Check, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLF_GREEN = '#2E6A4C';
const GOLD = '#D4AF37';

const getToken = () =>
  localStorage.getItem('client_portal_token') || localStorage.getItem('access_token') || '';

/**
 * Widget compte prépayé + achat bon cadeau — affiché en tête de l'onglet Boutique.
 *  - solde live
 *  - QR code (image PNG)
 *  - bouton "Recharger" (redirige vers /portail/mon-compte pour la vue détail complète)
 *  - bouton "Offrir un bon cadeau" (ouvre modal Stripe checkout)
 */
export default function PortalWalletWidget({ onNavigateToDetail, showWallet = true, showGiftCards = true }) {
  const [data, setData] = useState(null);
  const [qrSrc, setQrSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [myGiftCards, setMyGiftCards] = useState([]);

  const load = useCallback(async () => {
    if (!showWallet) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/pos/portal/my-account`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error('Erreur');
      const d = await r.json();
      setData(d);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [showWallet]);

  const loadQR = useCallback(async () => {
    if (!showWallet) return;
    try {
      const r = await fetch(`${API_URL}/api/pos/portal/my-qr-image?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) return;
      const blob = await r.blob();
      setQrSrc(URL.createObjectURL(blob));
    } catch (e) {
      /* silent */
    }
  }, [showWallet]);

  const loadGiftCards = useCallback(async () => {
    if (!showGiftCards) return;
    try {
      const r = await fetch(`${API_URL}/api/pos/gift-cards/portal/my-purchases`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) return;
      const d = await r.json();
      setMyGiftCards(d.gift_cards || []);
    } catch (e) {
      /* silent */
    }
  }, [showGiftCards]);

  useEffect(() => {
    load();
    loadQR();
    loadGiftCards();
  }, [load, loadQR, loadGiftCards]);

  // Retour Stripe : ?giftcard=success&session=xxx
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('giftcard') === 'success' && params.get('session')) {
      const sessionId = params.get('session');
      (async () => {
        try {
          const r = await fetch(`${API_URL}/api/pos/gift-cards/portal/verify/${sessionId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const d = await r.json();
          if (d.status === 'confirmed') {
            toast.success(`✓ Bon cadeau ${d.gift_card_number} activé !`);
            loadGiftCards();
          }
        } catch (e) { /* silent */ }
        window.history.replaceState({}, '', window.location.pathname);
      })();
    } else if (params.get('giftcard') === 'cancelled') {
      toast.info('Achat de bon cadeau annulé');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadGiftCards]);

  if (loading) {
    return (
      <div className="rounded-2xl p-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.92)', height: 120 }}>
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  // Aucune feature activée → widget invisible
  if (!showWallet && !showGiftCards) return null;

  // Cas 2 : Bons cadeaux uniquement (pas de wallet) → carte compacte
  if (!showWallet && showGiftCards) {
    return (
      <>
        <div
          className="rounded-2xl overflow-hidden p-4 flex items-center gap-4"
          style={{
            background: `linear-gradient(135deg, ${GOLF_GREEN} 0%, #1f4d37 100%)`,
            boxShadow: '0 4px 20px rgba(46,106,76,0.25)',
          }}
          data-testid="portal-gift-only-widget"
        >
          <div className="flex-1 text-white">
            <div className="flex items-center gap-1.5 mb-1 opacity-80">
              <Gift size={12} />
              <span className="text-[10px] uppercase font-semibold tracking-wider">Offrir un cadeau</span>
            </div>
            <div className="text-lg font-black">Bon cadeau Kempferhof</div>
            <p className="text-[11px] opacity-80 mt-0.5">Offrez du golf à vos proches</p>
          </div>
          <button
            onClick={() => setShowGiftModal(true)}
            className="px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all hover:scale-[1.02]"
            style={{ backgroundColor: GOLD, color: '#1a1a1a' }}
            data-testid="wallet-gift-btn"
          >
            <Gift size={13} /> Offrir un bon
          </button>
        </div>
        {myGiftCards.length > 0 && (
          <div className="rounded-2xl p-3 mt-2" style={{ backgroundColor: 'rgba(255,255,255,0.92)' }} data-testid="wallet-gifts-history">
            <div className="flex items-center gap-2 mb-2">
              <Gift size={13} style={{ color: GOLD }} />
              <span className="text-[11px] uppercase font-bold tracking-wider" style={{ color: '#666' }}>Mes bons cadeaux offerts</span>
            </div>
            <div className="space-y-2">
              {myGiftCards.slice(0, 5).map(gc => (
                <div key={gc.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg" style={{ backgroundColor: '#F5F2EB' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: gc.status === 'active' ? '#E8F5E9' : '#FEE' }}>
                    <Gift size={14} style={{ color: gc.status === 'active' ? GOLF_GREEN : '#c33' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{gc.recipient_name}</div>
                    <div className="text-[10px] text-neutral-500">
                      {gc.number} · <span className="font-mono">{gc.code}</span>
                      {gc.status !== 'active' && <span className="ml-1">· {gc.status}</span>}
                    </div>
                  </div>
                  <div className="text-sm font-bold" style={{ color: GOLF_GREEN }}>{Number(gc.amount_original).toFixed(0)}€</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {showGiftModal && (
          <GiftCardPurchaseModal
            onClose={() => setShowGiftModal(false)}
            onSuccess={() => { setShowGiftModal(false); loadGiftCards(); }}
          />
        )}
      </>
    );
  }

  const balance = Number(data?.balance || 0);
  const displayName = data?.customer_name || '';

  return (
    <>
      {/* Widget compte prépayé */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${GOLF_GREEN} 0%, #1f4d37 100%)`,
          boxShadow: '0 4px 20px rgba(46,106,76,0.25)',
        }}
        data-testid="portal-wallet-widget"
      >
        <div className="p-4 flex items-start gap-4 flex-wrap md:flex-nowrap">
          {/* Solde + boutons */}
          <div className="flex-1 min-w-[200px] text-white">
            <div className="flex items-center gap-1.5 mb-1 opacity-80">
              <Wallet size={12} />
              <span className="text-[10px] uppercase font-semibold tracking-wider">Compte prépayé</span>
            </div>
            <div className="text-3xl font-black" data-testid="wallet-balance">
              {balance.toFixed(2)}€
            </div>
            <p className="text-[11px] opacity-80 mt-0.5">Utilisable en caisse (green fee, boutique, restau…)</p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => onNavigateToDetail ? onNavigateToDetail() : (window.location.href = clubPath('/portail/mon-compte'))}
                className="flex-1 px-3 py-2 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition-all hover:scale-[1.02]"
                style={{ backgroundColor: GOLD, color: '#1a1a1a' }}
                data-testid="wallet-recharge-btn"
              >
                <TrendingUp size={13} /> Recharger
              </button>
              {showGiftCards && (
                <button
                  onClick={() => setShowGiftModal(true)}
                  className="px-3 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: 'rgba(255,255,255,0.16)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)' }}
                  data-testid="wallet-gift-btn"
                >
                  <Gift size={13} /> Offrir un bon
                </button>
              )}
            </div>
          </div>
          {/* QR code direct */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <div className="bg-white rounded-xl p-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
              {qrSrc ? (
                <img src={qrSrc} alt="QR" className="w-24 h-24 object-contain" data-testid="wallet-qr-img" />
              ) : (
                <div className="w-24 h-24 flex items-center justify-center text-neutral-400">
                  <QrCode size={40} />
                </div>
              )}
            </div>
            <p className="text-[9px] text-white opacity-75 mt-1 text-center leading-tight max-w-[96px]">
              Présentez ce QR<br />en caisse
            </p>
          </div>
        </div>
      </div>

      {/* Historique bons cadeaux offerts */}
      {showGiftCards && myGiftCards.length > 0 && (
        <div className="rounded-2xl p-3" style={{ backgroundColor: 'rgba(255,255,255,0.92)' }} data-testid="wallet-gifts-history">
          <div className="flex items-center gap-2 mb-2">
            <Gift size={13} style={{ color: GOLD }} />
            <span className="text-[11px] uppercase font-bold tracking-wider" style={{ color: '#666' }}>Mes bons cadeaux offerts</span>
          </div>
          <div className="space-y-2">
            {myGiftCards.slice(0, 5).map(gc => (
              <div key={gc.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg" style={{ backgroundColor: '#F5F2EB' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: gc.status === 'active' ? '#E8F5E9' : '#FEE' }}>
                  <Gift size={14} style={{ color: gc.status === 'active' ? GOLF_GREEN : '#c33' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{gc.recipient_name}</div>
                  <div className="text-[10px] text-neutral-500">
                    {gc.number} · <span className="font-mono">{gc.code}</span>
                    {gc.status !== 'active' && <span className="ml-1">· {gc.status}</span>}
                  </div>
                </div>
                <div className="text-sm font-bold" style={{ color: GOLF_GREEN }}>{Number(gc.amount_original).toFixed(0)}€</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal achat bon cadeau */}
      {showGiftCards && showGiftModal && (
        <GiftCardPurchaseModal
          onClose={() => setShowGiftModal(false)}
          onPurchased={() => { setShowGiftModal(false); loadGiftCards(); }}
        />
      )}
    </>
  );
}


function GiftCardPurchaseModal({ onClose, onPurchased }) {
  const [amount, setAmount] = useState('50');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [validity, setValidity] = useState(12);
  const [notifyRecipient, setNotifyRecipient] = useState(false);
  const [processing, setProcessing] = useState(false);

  const purchase = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 10) { toast.error('Montant minimum 10€'); return; }
    if (!recipientName.trim()) { toast.error('Nom du bénéficiaire requis'); return; }
    setProcessing(true);
    try {
      const origin = window.location.origin + window.location.pathname;
      const r = await fetch(`${API_URL}/api/pos/gift-cards/portal/purchase`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amt,
          recipient_name: recipientName.trim(),
          recipient_email: recipientEmail.trim() || undefined,
          message: message.trim() || undefined,
          validity_months: validity,
          origin_url: origin,
          notify_recipient: notifyRecipient,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        const det = d?.detail;
        const errMsg = Array.isArray(det) ? det.map(x => x?.msg || String(x)).join(', ') : (typeof det === 'string' ? det : 'Erreur');
        throw new Error(errMsg);
      }
      window.location.href = d.url;   // redirect vers Stripe
    } catch (e) {
      toast.error(e.message || 'Erreur');
      setProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="gift-card-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="p-4 flex items-center justify-between border-b"
          style={{ background: `linear-gradient(135deg, ${GOLD} 0%, #B8941E 100%)` }}
        >
          <div className="flex items-center gap-2 text-white">
            <Gift size={20} />
            <h3 className="font-bold">Offrir un bon cadeau</h3>
          </div>
          <button onClick={onClose} className="p-1 text-white hover:opacity-70">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {/* Montant */}
          <div>
            <label className="text-xs font-semibold text-neutral-600 block mb-1">Montant</label>
            <div className="flex gap-1.5 flex-wrap mb-2">
              {[20, 50, 100, 150, 200, 300, 500].map(v => (
                <button
                  key={v}
                  onClick={() => setAmount(String(v))}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${amount === String(v) ? 'text-white' : 'bg-neutral-100 hover:bg-neutral-200'}`}
                  style={amount === String(v) ? { backgroundColor: GOLF_GREEN } : {}}
                  data-testid={`gift-amount-${v}`}
                >
                  {v}€
                </button>
              ))}
            </div>
            <Input type="number" step="1" min="10" max="2000" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 text-lg font-bold text-center" data-testid="gift-amount-input" />
          </div>
          {/* Bénéficiaire */}
          <div>
            <label className="text-xs font-semibold text-neutral-600 block mb-1">Bénéficiaire *</label>
            <Input placeholder="Prénom Nom" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className="h-9 text-sm" data-testid="gift-recipient-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 block mb-1">Email du bénéficiaire (optionnel)</label>
            <Input type="email" placeholder="jean@exemple.fr" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} className="h-9 text-sm" data-testid="gift-recipient-email" />
            {/* Option envoi direct */}
            <label
              className={`mt-2 flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${notifyRecipient ? 'border-emerald-400 bg-emerald-50' : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'} ${!recipientEmail.trim() ? 'opacity-60 cursor-not-allowed' : ''}`}
              data-testid="gift-notify-recipient-label"
            >
              <input
                type="checkbox"
                checked={notifyRecipient}
                disabled={!recipientEmail.trim()}
                onChange={(e) => setNotifyRecipient(e.target.checked)}
                className="mt-0.5"
                data-testid="gift-notify-recipient"
              />
              <div className="flex-1">
                <div className="text-xs font-semibold text-neutral-800">Envoyer directement le bon au bénéficiaire</div>
                <div className="text-[10px] text-neutral-500 leading-tight mt-0.5">
                  {recipientEmail.trim()
                    ? 'Le PDF du bon cadeau sera envoyé à cet email après paiement. Décoché = vous recevez tout et faites la surprise en réel 🎁'
                    : 'Renseignez d\'abord l\'email du bénéficiaire pour activer cette option'}
                </div>
              </div>
            </label>
          </div>
          {/* Validité */}
          <div>
            <label className="text-xs font-semibold text-neutral-600 block mb-1">Validité</label>
            <div className="flex gap-1.5">
              {[6, 12, 18, 24].map(m => (
                <button
                  key={m}
                  onClick={() => setValidity(m)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${validity === m ? 'text-white' : 'bg-neutral-100 hover:bg-neutral-200'}`}
                  style={validity === m ? { backgroundColor: GOLF_GREEN } : {}}
                  data-testid={`gift-validity-${m}`}
                >
                  {m} mois
                </button>
              ))}
            </div>
          </div>
          {/* Message */}
          <div>
            <label className="text-xs font-semibold text-neutral-600 block mb-1">Message personnalisé (optionnel)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 300))}
              rows={3}
              placeholder="Joyeux anniversaire, profite de ton golf préféré !"
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200"
              data-testid="gift-message"
            />
            <p className="text-[10px] text-neutral-400 text-right">{message.length}/300</p>
          </div>
          <div className="rounded-lg p-2.5 text-[11px] flex items-start gap-2" style={{ backgroundColor: '#FEF9E7', color: '#7A5F0A' }}>
            <Send size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              {notifyRecipient
                ? 'Après paiement : vous recevrez le reçu + le PDF du bon, et votre bénéficiaire recevra automatiquement son bon cadeau par email.'
                : 'Après paiement : vous recevrez par email le reçu et le PDF du bon cadeau — vous pourrez le transférer ou l\'imprimer pour faire la surprise en réel.'}
            </span>
          </div>
          <Button
            onClick={purchase}
            disabled={processing || !recipientName.trim() || parseFloat(amount) < 10}
            className="w-full h-11"
            style={{ backgroundColor: GOLF_GREEN }}
            data-testid="gift-purchase-btn"
          >
            {processing ? (
              <><Loader2 size={14} className="animate-spin mr-2" /> Redirection…</>
            ) : (
              <><Gift size={14} className="mr-2" /> Payer {parseFloat(amount || 0).toFixed(0)}€ via Stripe</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
